import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import TopNav from '../components/TopNav';
import '../styles/MyEvents.css';

function lmsEntryStatusBadge(entry) {
  if (!entry) return null;
  if (entry.status === 'winner') return { label: 'You won', cls: 'badge-blue' };
  if (entry.status === 'eliminated') return { label: 'Eliminated', cls: 'badge-red' };
  if (entry.status === 'active') return { label: 'Alive', cls: 'badge-green' };
  if (entry.status === 'pending_payment') return { label: 'Alive (payment pending)', cls: 'badge-amber' };
  return null;
}

// Per-format_type behaviour, looked up by events.format_type so new sports
// slot in as data (add an adapter + real event rows) rather than new pages.
// See MY_EVENTS_HUB.md: "must not hardcode LMS."
//
// An event only ever appears here if the player has a relationship with it:
// either it's live/joinable right now (isLive, an event-level fact, true for
// any player), or the player actually has history with it (hasHistory, a
// personal fact -- an entry). No relationship, no card. The hub is personal
// to each player's history, not a global events list.
const EVENT_ADAPTERS = {
  lms: {
    href: (event) => `/lms/${event.id}`,
    isLive: (event, ctx) => {
      const round = ctx.roundsByEvent.get(event.id);
      return !!round && (round.status === 'open_for_entry' || round.status === 'active');
    },
    hasHistory: (event, ctx) => !!ctx.getMyLmsEntry(event.id),
    getBadge: (event, ctx) => {
      const myEntry = ctx.getMyLmsEntry(event.id);
      const entryBadge = lmsEntryStatusBadge(myEntry);
      if (entryBadge) return entryBadge;
      const round = ctx.roundsByEvent.get(event.id);
      if (round?.status === 'open_for_entry') return { label: 'Open — join now', cls: 'badge-blue' };
      if (round?.status === 'active') return { label: 'Live', cls: 'badge-green' };
      return { label: 'Round complete', cls: 'badge-gray' };
    },
    getStats: (event, ctx) => {
      const round = ctx.roundsByEvent.get(event.id);
      return round ? [`R${round.pot_total} pot`, `Round ${round.round_number}`] : [];
    }
  },
  world_cup: {
    href: () => '/world-cup',
    // World Cup never has a "join now" moment any more -- it only ever
    // shows up via hasHistory, i.e. only for players who actually entered.
    isLive: () => false,
    hasHistory: (event, ctx) => ctx.hasWorldCupEntry,
    getBadge: () => ({ label: 'Completed', cls: 'badge-gray' }),
    getStats: () => []
  }
};

function adapterFor(event) {
  return EVENT_ADAPTERS[event.format_type] || {
    href: () => null,
    isLive: () => false,
    hasHistory: () => false,
    getBadge: () => ({ label: event.status || 'Unavailable', cls: 'badge-gray' }),
    getStats: () => []
  };
}

export default function MyEvents() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [roundsByEvent, setRoundsByEvent] = useState(new Map());
  const [hasWorldCupEntry, setHasWorldCupEntry] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      // Logged-out visitors to predicta.buzz ("/") get the public marketing
      // landing (Home.js at /welcome), not straight to the login form -- so
      // the LMS explainer section actually lives at the real public root,
      // per the request that added it, rather than only being reachable if
      // someone already knows the /welcome URL.
      if (!authUser) { navigate('/welcome'); return; }

      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();
      setUser(userData);

      const [{ data: eventsData }, { data: lmsEntries }, { data: wcEntries }] = await Promise.all([
        supabase.from('events').select('*').order('created_at', { ascending: true }),
        supabase.from('lms_entries').select('*, lms_rounds(*, events(*))').eq('user_id', authUser.id),
        supabase.from('entries').select('id').eq('user_id', authUser.id).limit(1)
      ]);

      const allEvents = eventsData || [];
      setEvents(allEvents);
      setMyEntries(lmsEntries || []);
      setHasWorldCupEntry((wcEntries || []).length > 0);

      // events.status is hand-set and drifts out of sync with reality; the
      // real state lives on lms_rounds (public-read), so derive each LMS
      // event's live/badge state from its actual round instead.
      const lmsEventIds = allEvents.filter(e => e.format_type === 'lms').map(e => e.id);
      if (lmsEventIds.length > 0) {
        const { data: roundsData } = await supabase
          .from('lms_rounds')
          .select('*')
          .in('event_id', lmsEventIds)
          .order('round_number', { ascending: false });

        const byEvent = new Map();
        for (const r of roundsData || []) {
          if (!byEvent.has(r.event_id)) byEvent.set(r.event_id, []);
          byEvent.get(r.event_id).push(r);
        }
        const resolved = new Map();
        for (const [eventId, rounds] of byEvent) {
          const joinable = rounds.find(r => r.status === 'open_for_entry' || r.status === 'active');
          resolved.set(eventId, joinable || rounds[0] || null);
        }
        setRoundsByEvent(resolved);
      }

      setLoading(false);
    }
    fetchData();
  }, [navigate]);

  function getMyLmsEntry(eventId) {
    const candidates = myEntries.filter(e => e.lms_rounds?.events?.id === eventId);
    if (candidates.length === 0) return null;
    const alive = candidates.find(e => e.status === 'active' || e.status === 'pending_payment');
    if (alive) return alive;
    return candidates.slice().sort((a, b) => new Date(b.joined_at) - new Date(a.joined_at))[0];
  }

  const ctx = { getMyLmsEntry, roundsByEvent, hasWorldCupEntry };

  function handleEventClick(event) {
    const href = adapterFor(event).href(event);
    if (href) navigate(href);
  }

  if (loading) return <div className="my-events-loading">Loading...</div>;

  // Live takes priority: an event with a live round is shown there even if
  // the player also has past-round history within it (that history lives on
  // the event page, not duplicated as a second hub card).
  const liveEvents = events.filter(e => adapterFor(e).isLive(e, ctx));
  const pastEvents = events.filter(e => !adapterFor(e).isLive(e, ctx) && adapterFor(e).hasHistory(e, ctx));
  const firstName = user?.display_name || user?.name?.split(' ')[0];

  function EventCard({ event, active }) {
    const adapter = adapterFor(event);
    const badge = adapter.getBadge(event, ctx);
    const stats = adapter.getStats(event, ctx);
    return (
      <div
        key={event.id}
        className={`my-events-card ${active ? 'my-events-card-active' : ''}`}
        onClick={() => handleEventClick(event)}
      >
        <span className={`my-events-badge ${badge.cls}`}>{badge.label}</span>
        <p className="my-events-name">{event.name}</p>
        <p className="my-events-sport">{event.sport?.charAt(0).toUpperCase() + event.sport?.slice(1)}</p>
        {stats.length > 0 && (
          <div className="my-events-stats">
            {stats.map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <span className="my-events-stats-sep">·</span>}
                <span>{s}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="my-events-wrap">
      <TopNav active="my-events" />

      <div className="my-events-body">
        <h1 className="my-events-greeting">Welcome back, {firstName}</h1>

        {liveEvents.length > 0 && (
          <>
            <p className="my-events-section-title">Live events</p>
            <div className="my-events-grid">
              {liveEvents.map(event => <EventCard key={event.id} event={event} active />)}
            </div>
          </>
        )}

        {pastEvents.length > 0 && (
          <>
            <p className="my-events-section-title">Past events</p>
            <div className="my-events-grid">
              {pastEvents.map(event => <EventCard key={event.id} event={event} />)}
            </div>
          </>
        )}

        {liveEvents.length === 0 && pastEvents.length === 0 && (
          <div className="card">
            <p className="text-muted">No events yet. Once a round is open to join, it'll show up here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
